/**
 * Portal detail modal — renders the FULL row detail matching the main-app
 * "Report Incident" / "Edit DAR" modals, but read-only. Photo grid at the
 * bottom uses the same viewer treatment as the main app's photo modal.
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Modal } from "../components/Modal";
import { PillBadge, GhostButton } from "../components/Primitives";
import api, { errorMessage, API_BASE } from "../lib/api";
import AttachmentViewer, { isVideoAttachment } from "./AttachmentViewer";
import TourScanLogTable from "./TourScanLogTable";
import { Send, MessageSquare, Printer, Mail, X as XIcon } from "lucide-react";

// Tour scans are too brief to warrant a per-record PDF — the button is
// hidden for that kind.
const PDF_SUPPORTED_KINDS = new Set(["incidents", "maintenance", "dar"]);

function fmtDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch (err) { return iso; }
}

function KV({ label, value, mono = false }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div>
      <div className="label-mute">{label}</div>
      <div className={`text-zinc-100 mt-1 ${mono ? "font-mono text-xs" : "text-sm"}`}>{value}</div>
    </div>
  );
}

function _isVideoAttachment(p) {
  return isVideoAttachment(p);
}

function PhotoGrid({ items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  const photoCount = items.filter((p) => !_isVideoAttachment(p)).length;
  const videoCount = items.length - photoCount;
  const header = [
    photoCount > 0 ? `Photos (${photoCount})` : null,
    videoCount > 0 ? `Videos (${videoCount})` : null,
  ].filter(Boolean).join(" · ");
  return (
    <div>
      <div className="label-mute mb-2">{header}</div>
      <AttachmentViewer items={items} formatTimestamp={fmtDT} testIdPrefix="portal-detail" />
    </div>
  );
}


function IncidentDetailBody({ row }) {
  const sevTone = ({ critical: "red", high: "red", medium: "amber", low: "blue" })[row.severity] || "default";
  const statTone = ({ open: "red", investigating: "amber", resolved: "green" })[row.status] || "default";
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 flex-wrap">
        <PillBadge tone={sevTone}>{row.severity}</PillBadge>
        <PillBadge tone={statTone}>{row.status}</PillBadge>
        {row.incident_type && <PillBadge>{String(row.incident_type).replace("_", " ")}</PillBadge>}
        {row.injuries && <PillBadge tone="red">injuries</PillBadge>}
        {row.police_called && <PillBadge tone="amber">police</PillBadge>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KV label="Site" value={row.site_name} />
        <KV label="Client" value={row.client_name} />
        <KV label="Occurred at" value={fmtDT(row.occurred_at || row.created_at)} />
        <KV label="Logged" value={fmtDT(row.created_at)} />
        <KV label="Filed by" value={row.reported_by_name} />
        <KV label="Location on site" value={row.location_on_site} />
      </div>

      <div>
        <div className="label-mute mb-1">Narrative</div>
        <div className="text-sm text-zinc-200 whitespace-pre-line leading-relaxed border border-[#27272A] bg-[#0A0A0A] p-3">
          {row.description || "—"}
        </div>
      </div>

      {row.ai_summary && (
        <div className="border-l-2 border-[#FF3B30] bg-[#0A0A0A] p-3">
          <div className="label-mute mb-2">AI Summary (Claude)</div>
          <div className="text-sm text-zinc-300 whitespace-pre-line">{row.ai_summary}</div>
        </div>
      )}

      {(row.victim_name || row.suspect_description || (row.witness_names || []).length > 0) && (
        <div>
          <div className="label-mute mb-2">People involved</div>
          <div className="grid grid-cols-2 gap-4">
            <KV label="Victim" value={row.victim_name} />
            <KV label="Suspect" value={row.suspect_description} />
            {Array.isArray(row.witness_names) && row.witness_names.length > 0 && (
              <div className="col-span-2">
                <div className="label-mute">Witnesses</div>
                <ul className="list-disc list-inside text-sm text-zinc-200 mt-1">
                  {row.witness_names.map((w, i) => <li key={`witness-${w}-${i}`}>{w}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {(row.police_called || row.injuries || row.property_damage) && (
        <div>
          <div className="label-mute mb-2">Authorities & impact</div>
          <div className="grid grid-cols-2 gap-4">
            {row.police_called && <KV label="Police report #" value={row.police_report_no || "—"} />}
            {row.injuries && <KV label="Injuries" value={row.injuries_description || "Yes"} />}
            {row.property_damage && <KV label="Property damage" value={row.property_damage_description || "Yes"} />}
            {row.property_damage && row.property_damage_value != null && (
              <KV label="Estimated value" value={`$${Number(row.property_damage_value).toLocaleString()}`} />
            )}
          </div>
        </div>
      )}

      {(row.action_taken || row.follow_up_needed) && (
        <div>
          <div className="label-mute mb-2">Action & follow-up</div>
          <KV label="Action taken" value={row.action_taken} />
          {row.follow_up_needed && (
            <div className="mt-3">
              <KV label="Follow-up notes" value={row.follow_up_notes || "Follow-up flagged"} />
            </div>
          )}
        </div>
      )}

      <PhotoGrid items={row.photo_attachments} />
    </div>
  );
}


function MaintenanceDetailBody({ row }) {
  // Backend model uses `severity` (low/medium/high/critical); some legacy
  // rows may carry `priority` — support both so the UI stays lit either way.
  const sev = row.severity || row.priority;
  const sevTone = ({ critical: "red", high: "red", medium: "amber", low: "blue" })[sev] || "default";
  const statTone = ({ open: "red", in_progress: "amber", resolved: "green", verified: "green", closed: "green" })[row.status] || "default";
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 flex-wrap">
        {sev && <PillBadge tone={sevTone}>{sev}</PillBadge>}
        {row.status && <PillBadge tone={statTone}>{String(row.status).replace("_", " ")}</PillBadge>}
        {row.category && <PillBadge>{row.category}</PillBadge>}
        {row.follow_up_needed && <PillBadge tone="amber">follow-up</PillBadge>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KV label="Site" value={row.site_name} />
        <KV label="Client" value={row.client_name} />
        <KV label="Reported" value={fmtDT(row.created_at)} />
        <KV label="Reported by" value={row.reported_by_name || row.guard_name} />
        <KV label="Location on site" value={row.location_on_site} />
        {row.category && <KV label="Category" value={row.category} />}
      </div>

      <div>
        <div className="label-mute mb-1">Description</div>
        <div className="text-sm text-zinc-200 whitespace-pre-line leading-relaxed border border-[#27272A] bg-[#0A0A0A] p-3">
          {row.description || "—"}
        </div>
      </div>

      {row.ai_summary && (
        <div className="border-l-2 border-[#FF9F0A] bg-[#0A0A0A] p-3">
          <div className="label-mute mb-2">AI Summary (Claude)</div>
          <div className="text-sm text-zinc-300 whitespace-pre-line">{row.ai_summary}</div>
        </div>
      )}

      {(row.action_taken || row.follow_up_needed) && (
        <div>
          <div className="label-mute mb-2">Action & follow-up</div>
          <KV label="Action taken" value={row.action_taken} />
          {row.follow_up_needed && (
            <div className="mt-3">
              <KV label="Follow-up notes" value={row.follow_up_notes || "Follow-up flagged"} />
            </div>
          )}
        </div>
      )}

      <PhotoGrid items={row.photo_attachments} />
    </div>
  );
}


function DarDetailBody({ row }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 flex-wrap">
        {row.client_name && <PillBadge>{row.client_name}</PillBadge>}
        {row.guard_name && <PillBadge tone="blue">{row.guard_name}</PillBadge>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KV label="Site" value={row.site_name} />
        <KV label="Officer" value={row.guard_name} />
        <KV label="Submitted" value={fmtDT(row.created_at)} />
        <KV label="Visitors" value={row.visitor_count ?? 0} mono />
        <KV label="Vehicles" value={row.vehicle_count ?? 0} mono />
        <KV label="Weather" value={row.weather_conditions} />
      </div>

      <div>
        {row.dar_mode === "detailed" ? (
          <>
            <div className="flex items-center justify-between mb-1">
              <div className="label-mute">Tour Scan Log · Detailed DAR</div>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                {(row.tour_scan_log || []).length} scan{(row.tour_scan_log || []).length === 1 ? "" : "s"}
              </span>
            </div>
            <TourScanLogTable
              rows={row.tour_scan_log}
              siteTimezone={row.site_timezone}
              testIdPrefix="portal-dar-scan-log"
            />
            <details className="mt-3 text-sm text-zinc-400 group">
              <summary
                className="cursor-pointer select-none uppercase tracking-widest text-[10px] text-zinc-500 hover:text-zinc-300"
                data-testid="portal-dar-narrative-toggle"
              >
                <span className="group-open:hidden">Show narrative ▾</span>
                <span className="hidden group-open:inline">Hide narrative ▴</span>
              </summary>
              <div className="mt-2 whitespace-pre-line border border-[#27272A] bg-[#0A0A0A] p-3">
                {row.narrative || <span className="italic text-zinc-500">No narrative provided.</span>}
              </div>
            </details>
          </>
        ) : (
          <>
            <div className="label-mute mb-1">Narrative</div>
            <div className="text-sm text-zinc-200 whitespace-pre-line leading-relaxed border border-[#27272A] bg-[#0A0A0A] p-3">
              {row.narrative || "—"}
            </div>
          </>
        )}
      </div>

      {row.ai_summary && (
        <div
          className="border-l-2 border-[#0A84FF] bg-[#0A0A0A] p-3"
          data-testid="portal-dar-ai-summary"
        >
          <div className="label-mute mb-2">AI Summary (Claude)</div>
          <div className="text-sm text-zinc-300 whitespace-pre-line">{row.ai_summary}</div>
        </div>
      )}

      {Array.isArray(row.equipment_inspected) && row.equipment_inspected.length > 0 && (
        <div>
          <div className="label-mute mb-2">Equipment inspected</div>
          <div className="flex flex-wrap gap-1">
            {row.equipment_inspected.map((e) => <PillBadge key={e}>{e}</PillBadge>)}
          </div>
        </div>
      )}

      {row.maintenance_issues && (
        <div>
          <div className="label-mute mb-1">Maintenance issues observed</div>
          <div className="text-sm text-zinc-200 whitespace-pre-line border border-[#27272A] bg-[#0A0A0A] p-3">{row.maintenance_issues}</div>
        </div>
      )}

      {Array.isArray(row.notable_events) && row.notable_events.length > 0 && (
        <div>
          <div className="label-mute mb-2">Notable events</div>
          <div className="border border-[#27272A]">
            {row.notable_events.map((e, i) => (
              <div key={`${e.at || "no-ts"}-${i}`} className="px-3 py-2 border-b border-[#27272A] last:border-b-0 flex gap-3 text-sm">
                <div className="text-zinc-500 font-mono text-xs whitespace-nowrap min-w-[130px]">{fmtDT(e.at)}</div>
                <div className="text-zinc-200 flex-1">{e.text}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {row.handover_notes && (
        <div>
          <div className="label-mute mb-1">Handover notes</div>
          <div className="text-sm text-zinc-200 whitespace-pre-line border border-[#27272A] bg-[#0A0A0A] p-3">{row.handover_notes}</div>
        </div>
      )}

      <PhotoGrid items={row.photo_attachments} />
    </div>
  );
}


function TourScanDetailBody({ row }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 flex-wrap">
        <PillBadge tone="blue">{(row.method || "").toUpperCase()}</PillBadge>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KV label="Site" value={row.site_name} />
        <KV label="Stop / checkpoint" value={row.stop_name} />
        <KV label="Method" value={String(row.method || "").toUpperCase()} />
        <KV label="Officer" value={row.guard_name} />
        <KV label="Captured at" value={fmtDT(row.captured_at || row.created_at)} />
        {row.lat != null && row.lng != null && (
          <KV label="GPS" value={`${Number(row.lat).toFixed(6)}, ${Number(row.lng).toFixed(6)}`} mono />
        )}
      </div>
      {row.notes && (
        <div>
          <div className="label-mute mb-1">Notes</div>
          <div className="text-sm text-zinc-200 whitespace-pre-line border border-[#27272A] bg-[#0A0A0A] p-3">{row.notes}</div>
        </div>
      )}
    </div>
  );
}


const BODIES = {
  incidents:   { Comp: IncidentDetailBody,   titleOf: (r) => r.title || "Incident details" },
  maintenance: { Comp: MaintenanceDetailBody, titleOf: (r) => r.title || r.subject || "Maintenance request" },
  dar:         { Comp: DarDetailBody,         titleOf: (r) => `DAR · ${r.site_name || "—"}` },
  tour_scans:  { Comp: TourScanDetailBody,    titleOf: (r) => r.stop_name || "Tour scan" },
};


export default function PortalReportDetail({ kind, row, onClose, commentsEnabled }) {
  if (!row) return null;
  const meta = BODIES[kind];
  if (!meta) return null;
  const Body = meta.Comp;
  const showPdf = PDF_SUPPORTED_KINDS.has(kind);
  return (
    <Modal
      title={meta.titleOf(row)}
      onClose={onClose}
      testId="portal-report-detail-modal"
      wide
    >
      {showPdf && (
        <div className="flex justify-end gap-2 mb-4">
          <EmailButton kind={kind} row={row} />
          <PrintButton kind={kind} row={row} />
        </div>
      )}
      <Body row={row} />
      {commentsEnabled && (
        <div className="mt-8 pt-6 border-t border-[#27272A]">
          <CommentsSection kind={kind} targetId={row.id} />
        </div>
      )}
    </Modal>
  );
}


// ─── Per-report Print button ──────────────────────────────────────
function PrintButton({ kind, row }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const res = await fetch(
        `${API_BASE}/portal/reports/${kind}/${row.id}.pdf`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Nice filename: kind + short id + date
      const shortId = String(row.id).slice(0, 8);
      const date = new Date().toISOString().slice(0, 10);
      a.download = `${kind}_${shortId}_${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Download failed");
    } finally { setBusy(false); }
  };
  return (
    <GhostButton onClick={onClick} disabled={busy} data-testid="portal-print-report">
      <Printer className="size-3.5" /> {busy ? "Preparing…" : "Print to PDF"}
    </GhostButton>
  );
}


// ─── Per-report Email button + modal ──────────────────────────────
function EmailButton({ kind, row }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <GhostButton onClick={() => setOpen(true)} data-testid="portal-email-report">
        <Mail className="size-3.5" /> Email
      </GhostButton>
      {open && <EmailReportModal kind={kind} row={row} onClose={() => setOpen(false)} />}
    </>
  );
}


const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function EmailReportModal({ kind, row, onClose }) {
  const [chips,   setChips]   = useState([]);    // string[] validated addresses
  const [input,   setInput]   = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const commit = (raw) => {
    const parts = (raw || "").split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    const next = new Set(chips);
    let rejected = 0;
    parts.forEach((p) => {
      if (EMAIL_RE.test(p) && !next.has(p.toLowerCase()) && next.size + 1 <= 10) {
        next.add(p.toLowerCase());
      } else {
        rejected += 1;
      }
    });
    setChips([...next]);
    setInput("");
    if (rejected) toast.error(`${rejected} invalid or duplicate ${rejected === 1 ? "email" : "emails"} skipped`);
  };

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(input);
    } else if (e.key === "Backspace" && !input && chips.length > 0) {
      // Backspace on empty input removes the last chip
      setChips(chips.slice(0, -1));
    }
  };

  const removeChip = (email) => setChips(chips.filter((c) => c !== email));

  const canSend = chips.length > 0 && !sending;

  const onSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const body = { to: chips };
      if (subject.trim()) body.subject = subject.trim();
      if (message.trim()) body.message = message.trim();
      const r = await api.post(`/portal/reports/${kind}/${row.id}/email`, body);
      toast.success(`Report emailed to ${r.data.recipients.length} recipient${r.data.recipients.length === 1 ? "" : "s"}`);
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally { setSending(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
      data-testid="portal-email-report-modal"
    >
      <div className="border border-[#27272A] bg-[#121212] w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#27272A] flex items-center justify-between">
          <div>
            <div className="label-mute">Email report</div>
            <h3 className="font-display text-xl uppercase font-bold tracking-tight">Forward as PDF</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:text-[#FF3B30] transition-colors">
            <XIcon className="size-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <div className="label-mute mb-1">Recipients <span className="text-zinc-500 normal-case tracking-normal">(comma or Enter to add · max 10)</span></div>
            <div className="border border-[#27272A] bg-[#0A0A0A] p-2 flex flex-wrap gap-1.5 min-h-[42px] focus-within:border-[#FF3B30]">
              {chips.map((c) => (
                <span key={c} className="inline-flex items-center gap-1 border border-[#27272A] bg-[#141414] text-xs text-zinc-200 px-2 py-1 font-mono" data-testid={`portal-email-chip-${c}`}>
                  {c}
                  <button
                    type="button"
                    onClick={() => removeChip(c)}
                    className="text-zinc-500 hover:text-[#FF3B30]"
                    aria-label={`Remove ${c}`}
                  >
                    <XIcon className="size-3" />
                  </button>
                </span>
              ))}
              <input
                data-testid="portal-email-recipients-input"
                type="email"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                onBlur={() => input && commit(input)}
                placeholder={chips.length === 0 ? "insurance@example.com" : ""}
                className="flex-1 min-w-[140px] bg-transparent text-sm text-zinc-100 outline-none placeholder-zinc-600"
              />
            </div>
          </div>

          <div>
            <div className="label-mute mb-1">Subject <span className="text-zinc-500 normal-case tracking-normal">(optional)</span></div>
            <input
              data-testid="portal-email-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Leave blank for auto subject"
              className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#FF3B30] px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 outline-none"
              maxLength={200}
            />
          </div>

          <div>
            <div className="label-mute mb-1">Message <span className="text-zinc-500 normal-case tracking-normal">(optional cover note)</span></div>
            <textarea
              data-testid="portal-email-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Add a note for the recipient…"
              className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#FF3B30] px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 outline-none resize-none"
              maxLength={2000}
            />
            <div className="text-xs text-zinc-500 font-mono text-right mt-1">{message.length} / 2000</div>
          </div>

          <div className="text-xs text-zinc-500 border-l-2 border-[#27272A] pl-3">
            The report will be attached as a PDF. Recipients will see all addresses on the To: line. Replies come back to your email address.
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#27272A] flex items-center justify-end gap-2">
          <GhostButton onClick={onClose} disabled={sending}>Cancel</GhostButton>
          <GhostButton onClick={onSend} disabled={!canSend} data-testid="portal-email-send">
            <Send className="size-3.5" /> {sending ? "Sending…" : "Send"}
          </GhostButton>
        </div>
      </div>
    </div>
  );
}


// ─── Comments section ─────────────────────────────────────────────
function fmtCommentTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch (err) { return iso; }
}

function CommentsSection({ kind, targetId }) {
  const [rows, setRows] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/portal/comments", { params: { kind, target_id: targetId } });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      setRows([]);
    } finally { setLoading(false); }
  }, [kind, targetId]);

  useEffect(() => { load(); }, [load]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await api.post("/portal/comments", { kind, target_id: targetId, body: trimmed });
      setBody("");
      await load();
      toast.success("Comment posted");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="portal-comments-section">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="size-4 text-zinc-400" />
        <div className="label-mute">Comments {rows.length > 0 && <span className="text-zinc-400 ml-1">({rows.length})</span>}</div>
      </div>

      {loading ? (
        <div className="text-xs text-zinc-500 py-3">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-zinc-500 py-3 italic">No comments yet. Leave the first one below.</div>
      ) : (
        <div className="space-y-2 mb-4" data-testid="portal-comments-list">
          {rows.map((c) => (
            <div key={c.id} className="border border-[#27272A] bg-[#0A0A0A] p-3" data-testid={`portal-comment-${c.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-zinc-100 text-sm">{c.user_name}</span>
                <span className="text-xs text-zinc-500 font-mono">{fmtCommentTime(c.created_at)}</span>
              </div>
              <div className="text-sm text-zinc-200 whitespace-pre-line">{c.body}</div>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-2">
        <textarea
          data-testid="portal-comment-input"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Add a comment for the security team…"
          rows={3}
          className="w-full text-sm bg-[#0A0A0A] border border-[#27272A] focus:border-[#FF3B30] px-3 py-2 outline-none text-zinc-100 placeholder-zinc-600 resize-none"
          maxLength={5000}
        />
        <div className="flex items-center justify-between">
          <div className="text-xs text-zinc-500 font-mono">{body.length} / 5000</div>
          <GhostButton
            type="submit"
            disabled={submitting || !body.trim()}
            data-testid="portal-comment-submit"
          >
            <Send className="size-3.5" /> {submitting ? "Posting…" : "Post comment"}
          </GhostButton>
        </div>
      </form>
    </div>
  );
}
